import type { ReactNode } from "react";
import { Link } from "react-router";
import { LayoutGrid, List, type LucideIcon } from "lucide-react";
import type { ListingCard } from "../../api/catalogue";
import type { ListingView } from "../../lib/listingView";
import { formatPesos } from "../../lib/money";
import { Icon } from "../ui";
import { cx } from "../ui/cx";
import { StockBadge, unitLabel } from "./partLabels";
import { ProductCard } from "./ProductCard";

/** A listing as a department page or search shows it. Search adds the machines each fits. */
export type ResultListing = ListingCard & { fits?: string[] };

/** "default" is the order the server gave: by name, or by how well it matched. */
export type ListingSort = "default" | "size" | "price";

export interface ListingResultsProps {
  listings: ResultListing[];
  /** What the server's own order is called: "Name", "Best match", "Part number". */
  defaultSortLabel: string;
  sort: ListingSort;
  onSort: (sort: ListingSort) => void;
  view: ListingView;
  onView: (view: ListingView) => void;
  /** Left of the controls, like "14 parts". */
  summary?: ReactNode;
  /** Each listing's add-to-basket control; compact in a list row. */
  add: (listing: ResultListing, compact: boolean) => ReactNode;
}

/**
 * Parts as product cards, or as a list for comparing sizes (KALI-33). Every page opens as
 * cards; the list is the visitor's own choice. Sorting by size or price works in both.
 */
export function ListingResults({ listings, defaultSortLabel, sort, onSort, view, onView, summary, add }: ListingResultsProps) {
  const shown = sortListings(listings, sort);
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="mr-auto">{summary}</div>
        <div className="flex items-center gap-2" role="group" aria-label="Sort">
          <span className="type-eyebrow tracking-[var(--tracking-wider)] text-ink-500 uppercase">Sort</span>
          <ToggleButton active={sort === "default"} onClick={() => onSort("default")}>
            {defaultSortLabel}
          </ToggleButton>
          <ToggleButton active={sort === "size"} onClick={() => onSort("size")}>
            Size
          </ToggleButton>
          <ToggleButton active={sort === "price"} onClick={() => onSort("price")}>
            Price
          </ToggleButton>
        </div>
        <div className="flex" role="group" aria-label="Show as">
          <ViewButton icon={LayoutGrid} active={view === "cards"} onClick={() => onView("cards")} className="rounded-l-sm">
            Cards
          </ViewButton>
          <ViewButton icon={List} active={view === "list"} onClick={() => onView("list")} className="-ml-px rounded-r-sm">
            List
          </ViewButton>
        </div>
      </div>
      {view === "cards" ? (
        <ul className="m-0 grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-3 lg:grid-cols-4 lg:gap-6 xl:grid-cols-5">
          {shown.map((listing) => (
            <li key={listing.id} className="flex">
              <ProductCard listing={listing} detail={fitsLine(listing.fits)} footer={add(listing, false)} className="w-full" />
            </li>
          ))}
        </ul>
      ) : (
        <ListingList listings={shown} add={add} />
      )}
    </div>
  );
}

/** Name, size, price, stock and Add on one row, to compare parts that differ by size. */
function ListingList({ listings, add }: { listings: ResultListing[]; add: ListingResultsProps["add"] }) {
  const columns = "md:grid-cols-[minmax(0,1fr)_120px_120px_120px_96px]";
  return (
    <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface-card shadow-sm">
      <div className={cx("hidden gap-4 border-b border-border-subtle bg-paper-2 px-4 py-2.5 md:grid", columns)}>
        {["Part", "Size", "Price", "Stock"].map((label) => (
          <span key={label} className={cx("type-eyebrow tracking-[var(--tracking-wider)] text-ink-600 uppercase", label === "Price" && "text-right")}>
            {label}
          </span>
        ))}
      </div>
      <ul className="m-0 list-none p-0">
        {listings.map((listing) => (
          <li key={listing.id} className={cx("grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 border-t border-border-subtle px-4 py-3 first:border-t-0", columns)}>
            <Link to={`/listings/${listing.id}`} className="min-w-0 border-b-0 text-inherit hover:text-green-800">
              <span className="block type-body-strong text-text-strong">
                {withoutSize(listing.name, listing.dimension)}
                {listing.dimension && <span className="ml-2 type-sku [font-size:var(--text-xs)] text-ink-600 md:hidden">{listing.dimension}</span>}
              </span>
              <span className="block type-sku [font-size:var(--text-xs)] [overflow-wrap:anywhere] text-text-muted">{listing.reference_number ?? listing.sku}</span>
              {listing.fits && listing.fits.length > 0 && <span className="block type-small text-text-muted">{fitsLine(listing.fits)}</span>}
              {listing.replaced_by && <span className="block type-small text-warning-700">Replaced by {listing.replaced_by.sku}</span>}
            </Link>
            <span className="hidden type-sku text-text-strong md:block">{listing.dimension ?? "—"}</span>
            <span className="flex flex-col items-end md:block md:text-right">
              <span className="type-price [font-size:var(--text-base)] whitespace-nowrap text-text-strong">{formatPesos(listing.price_cents)}</span>
              <span className="type-small text-text-muted md:ml-1">{unitLabel(listing.unit)}</span>
            </span>
            <span className="hidden md:block">
              <StockBadge inStock={listing.in_stock} />
            </span>
            <span className="col-span-2 flex justify-end md:col-span-1">{add(listing, true)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ToggleButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        "min-h-9 max-md:min-h-11 cursor-pointer rounded-sm border px-3 type-label [font-size:var(--text-xs)]",
        active ? "border-green-700 bg-green-50 text-green-800" : "border-border-default bg-surface-card text-text-body",
      )}
    >
      {children}
    </button>
  );
}

function ViewButton({ icon, active, onClick, className, children }: { icon: LucideIcon; active: boolean; onClick: () => void; className?: string; children: string }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        "relative flex min-h-9 max-md:min-h-11 cursor-pointer items-center gap-1.5 border px-3 type-label [font-size:var(--text-xs)]",
        active ? "z-10 border-green-700 bg-green-700 text-white" : "border-border-default bg-surface-card text-text-body",
        className,
      )}
    >
      <Icon icon={icon} size={16} />
      {children}
    </button>
  );
}

function sortListings(listings: ResultListing[], sort: ListingSort) {
  if (sort === "size") return [...listings].sort(compareSizes);
  if (sort === "price") return [...listings].sort((a, b) => a.price_cents - b.price_cents);
  return listings;
}

/** Sizes in natural order: M6×8 before M8×12 before M8×33.5. Parts without one go last. */
function compareSizes(a: ResultListing, b: ResultListing) {
  if (!a.dimension || !b.dimension) return a.dimension ? -1 : b.dimension ? 1 : 0;
  return a.dimension.localeCompare(b.dimension, undefined, { numeric: true });
}

function fitsLine(fits: string[] | undefined) {
  if (!fits || fits.length === 0) return undefined;
  return `Fits ${fits.length <= 2 ? fits.join(" · ") : `${fits.slice(0, 2).join(" · ")} +${fits.length - 2}`}`;
}

/**
 * The name without its size, where the list shows the size beside it: "Bolt M6x40 2-3kW
 * Alternator" with size M6×40 reads "Bolt 2-3kW Alternator". The book writes x or ×.
 */
export function withoutSize(name: string, dimension: string | null) {
  if (!dimension) return name;
  const pattern = dimension.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/[×xX]/g, "[×xX]");
  const stripped = name.replace(new RegExp(`(^|\\s)${pattern}(?=[\\s,]|$)`, "i"), " ").replace(/\s+/g, " ").trim();
  return stripped || name;
}
